import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, UploadCloud, FileText, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { config } from '../../utils/config';
import { apiClient } from '../../api/client';
import InvoicesSubNav from '../InvoicesSubNav';

interface DetectedField {
  name: string;
  value: string;
  type: string;
  coordinates: { x: number; y: number; width: number; height: number };
  included: boolean;
}

type Phase = 'upload' | 'reading' | 'confirm' | 'error';

// Prefer the backend's friendly "detail" message (e.g. the template-limit
// notice) over axios's generic "Request failed with status code 403".
function getApiError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return err instanceof Error ? err.message : fallback;
}

const AddTemplatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const replaceId = (location.state as { replaceId?: string } | null)?.replaceId;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<Phase>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [fields, setFields] = useState<DetectedField[]>([]);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const goToList = () => navigate('/uploaded-invoices/templates');

  const handleFile = async (file: File) => {
    if (!file) return;
    // The file picker declares accept="application/pdf" but a drop bypasses it,
    // so a dropped .png was uploaded and came back as a generic read failure.
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Templates are built from PDF invoices. Choose a PDF file.');
      setPhase('error');
      return;
    }
    setPhase('reading');
    setError('');
    try {
      const form = new FormData();
      form.append('pdf_file', file);
      const response = await apiClient.post(
        `${config.endpoints.templates}/auto-generate`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const data = response.data;
      setTemplateId(data.id);
      setName(data.name || '');
      setFields((data.fields || []).map((f: Omit<DetectedField, 'included'>) => ({ ...f, included: true })));
      setPhase('confirm');
    } catch (err: unknown) {
      setError(getApiError(err, 'We could not read that file. Try a clearer PDF of a typical invoice.'));
      setPhase('error');
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const toggleField = (index: number) => {
    setFields(prev => prev.map((f, i) => (i === index ? { ...f, included: !f.included } : f)));
  };

  const handleSave = async () => {
    if (!templateId) return;
    const kept = fields.filter(f => f.included);
    if (kept.length === 0) {
      setError('Keep at least one detail so the template can help.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await apiClient.put(`${config.endpoints.templates}/${templateId}`, {
        name: name.trim() || 'New supplier template',
        status: 'active',
        zones: kept.map((f, i) => ({
          id: `zone-${i}`,
          name: f.name,
          type: f.type,
          coordinates: f.coordinates,
          required: false,
          rules: [],
        })),
      });
      // If this run replaces an older sample, remove the previous template.
      if (replaceId && replaceId !== templateId) {
        try {
          await apiClient.delete(`${config.endpoints.templates}/${replaceId}`);
        } catch {
          // best effort
        }
      }
      navigate(`/uploaded-invoices/templates/${templateId}`);
    } catch (err: unknown) {
      setError(getApiError(err, 'Could not save the template. Please try again.'));
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    // The template was created on upload; remove it if the user backs out.
    if (templateId) {
      try {
        await apiClient.delete(`${config.endpoints.templates}/${templateId}`);
      } catch {
        // best effort
      }
    }
    goToList();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <InvoicesSubNav />

      <div className="max-w-2xl mx-auto mt-6">
        <button
          onClick={phase === 'confirm' ? handleCancel : goToList}
          className="flex items-center gap-2 text-foreground-muted hover:text-foreground-secondary mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to templates
        </button>

        <h1 className="text-2xl font-bold text-white mb-2">Add a template</h1>

        {/* Upload */}
        {phase === 'upload' && (
          <>
            <p className="text-sm text-foreground-muted mb-6">
              Upload one invoice from a supplier you get bills from often. We'll read it and remember where
              each detail is, so their future invoices are read more accurately.
            </p>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${
                dragOver ? 'border-coral-400 bg-coral-500/10' : 'border-coral-500/30 hover:bg-coral-500/5'
              }`}
            >
              <UploadCloud className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
              <p className="text-foreground font-medium">Drop an invoice here, or click to choose a file</p>
              <p className="text-foreground-muted text-sm mt-1">PDF works best</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={onInputChange}
                className="hidden"
              />
            </div>
          </>
        )}

        {/* Reading */}
        {phase === 'reading' && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader className="w-8 h-8 text-coral-400 animate-spin mb-4" />
            <p className="text-foreground-secondary">Reading your invoice…</p>
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="py-8">
            <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-200 mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
            <button
              onClick={() => { setPhase('upload'); setError(''); }}
              className="px-5 py-2.5 bg-coral-500 hover:bg-coral-600 rounded-lg text-white font-medium transition-colors"
            >
              Try another file
            </button>
          </div>
        )}

        {/* Confirm */}
        {phase === 'confirm' && (
          <>
            <p className="text-sm text-foreground-muted mb-6 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              We found these details. Untick anything that looks wrong, then save.
            </p>

            <label className="block text-sm text-foreground-secondary mb-1">Supplier name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Office Supplies"
              className="w-full mb-6 px-4 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
            />

            <div className="space-y-2 mb-6">
              {fields.map((field, index) => (
                <label
                  key={`${field.name}-${index}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-coral-500/5 border border-coral-500/15 cursor-pointer hover:bg-coral-500/10"
                >
                  <input
                    type="checkbox"
                    checked={field.included}
                    onChange={() => toggleField(index)}
                    className="w-4 h-4 accent-coral-500"
                  />
                  <FileText className="w-4 h-4 text-coral-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground">{field.name}</p>
                    {field.value && (
                      <p className="text-xs text-foreground-muted truncate">{field.value}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-200 mb-4 text-sm">
                <AlertCircle className="w-4 h-4" />
                <p>{error}</p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 bg-coral-500 hover:bg-coral-600 disabled:opacity-50 rounded-lg text-white font-medium transition-colors flex items-center gap-2"
              >
                {saving && <Loader className="w-4 h-4 animate-spin" />}
                Save template
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-5 py-2.5 text-foreground-muted hover:text-foreground-secondary disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddTemplatePage;
